import React, { useState } from "react";
import { Alert, Box, Chip, MenuItem, Stack, TableBody, TableRow, TextField, Typography } from "@mui/material";
import { PageTitle } from "components/common/Text";
import { PrimaryButton } from "components/common/Buttons";
import CustomTable from "components/table/table.component";
import { TableBodyCell } from "components/table/table.style";
import useFirmwareBundles from "hooks/useFirmwareBundles";
import { canPairFirmwareBundle, requiresFirmwareBundle } from "utils/firmware-bundles";
import { showError, showSuccess } from "utils/notifications";
import { approveFirmwareBundle, ManagedModemFirmware, ManagedSoftwareVersion } from "./manage-versions.api";

export default function FirmwareBundles({
  versions,
  images,
}: {
  versions: ManagedSoftwareVersion[];
  images: ManagedModemFirmware[];
}): JSX.Element {
  const { bundles, error, isLoading, mutate } = useFirmwareBundles();
  const [version, setVersion] = useState("");
  const [image, setImage] = useState("");
  const [saving, setSaving] = useState(false);
  const deviceVersions = versions.filter(
    (item) => canPairFirmwareBundle(item.swVersion) && item.swVersion === item.approvedSwVersion,
  );
  const modemImages = images.filter(
    (item) =>
      ["B14", "B28"].includes(item.targetBand) && (!requiresFirmwareBundle(version) || item.targetBand === "B28"),
  );

  const approve = async () => {
    setSaving(true);
    try {
      await approveFirmwareBundle(version, image);
      await mutate();
      showSuccess("Firmware pair approved");
    } catch (err: any) {
      showError(err.response?.data?.error ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ mt: 4, mb: 4 }}>
      <PageTitle>Approved Firmware Packages</PageTitle>
      <Typography variant="body2" color="text.secondary">
        Manage the approved device and modem firmware pairs used for updates.
      </Typography>
      {error && <Alert severity="error">Could not load approved firmware packages. Please try again.</Alert>}
      {!isLoading && !error && bundles.length === 0 && (
        <Alert severity="info">
          No firmware packages have been approved. Updates to 3.9.1 and later are unavailable until a pair is approved.
        </Alert>
      )}
      {bundles.length > 0 && (
        <CustomTable boxShadow>
          <CustomTable.Header
            columns={[
              { id: "softwareVersion", label: "Device Firmware" },
              { id: "targetBand", label: "Target Band" },
              { id: "modemVersion", label: "Modem Firmware" },
              { id: "modemFirmware", label: "Approved Modem Image" },
            ]}
          />
          <TableBody>
            {bundles.map((bundle) => (
              <TableRow key={bundle.softwareVersion} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                <TableBodyCell sx={{ minWidth: 180, verticalAlign: "top" }}>
                  <Typography variant="body2" fontWeight={600}>
                    {bundle.softwareVersion}
                  </Typography>
                </TableBodyCell>
                <TableBodyCell sx={{ verticalAlign: "top" }}>
                  <Chip label={bundle.targetBand} color="primary" variant="outlined" size="small" />
                </TableBodyCell>
                <TableBodyCell sx={{ minWidth: 200, verticalAlign: "top" }}>
                  <Typography variant="body2">{bundle.modemVersion}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bundle.modemModel}
                  </Typography>
                </TableBodyCell>
                <TableBodyCell sx={{ maxWidth: 420, overflowWrap: "anywhere", verticalAlign: "top" }}>
                  <Typography variant="body2" color="text.secondary">
                    {bundle.modemFirmware}
                  </Typography>
                </TableBodyCell>
              </TableRow>
            ))}
          </TableBody>
        </CustomTable>
      )}
      <Stack
        spacing={2}
        sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 }}
      >
        <Box>
          <Typography component="h3" variant="subtitle1" fontWeight={600}>
            Approve Firmware Pair
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select registered images to create or replace a pair. Device firmware 3.9.1 and later requires B28.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 2fr)" },
            gap: 2,
          }}
        >
          <TextField
            select
            fullWidth
            label="Device firmware"
            value={version}
            onChange={(event) => {
              setVersion(event.target.value);
              setImage("");
            }}
            disabled={saving}
          >
            {deviceVersions.map((item) => (
              <MenuItem key={item.swVersion} value={item.swVersion}>
                {item.portalVersion}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Approved modem image"
            value={image}
            onChange={(event) => setImage(event.target.value)}
            disabled={saving}
          >
            {modemImages.map((item) => (
              <MenuItem
                key={item.eiSoftwareImageId}
                value={item.fileName}
                sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
              >
                {item.targetBand}: {item.fileName}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <PrimaryButton
          onClick={approve}
          disabled={saving || isLoading || Boolean(error) || !version || !image}
          sx={{ alignSelf: "flex-start" }}
        >
          {saving ? "Approving..." : "Approve Pair"}
        </PrimaryButton>
      </Stack>
    </Stack>
  );
}
